import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../services/adminApiService';
import type { AdminCustomer, ComplianceAlert } from '../../services/adminApiService';
import './AdminComponents.css';

interface UserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  alert: ComplianceAlert | null;
  onAssign: (alertId: string, assigneeId: string) => Promise<void>;
  onUpdateStatus: (alertId: number, status: string, notes?: string) => Promise<void>;
}

interface KycKybSummary {
  hasUtilityBill?: boolean;
  hasPassport?: boolean;
  hasIdCard?: boolean;
  hasMemorandum?: boolean;
  hasArticles?: boolean;
  hasBusinessUtilityBill?: boolean;
  submittedDate?: string;
  status: string;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  isOpen,
  onClose,
  alert,
  onAssign,
  onUpdateStatus
}) => {
  const [user, setUser] = useState<AdminCustomer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'personal' | 'documents' | 'activity'>('personal');
  const [assigneeId, setAssigneeId] = useState('');
  const [notes, setNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (isOpen && alert?.customerId) {
      loadUserDetails();
    }
  }, [isOpen, alert?.customerId]);

  // Debug avatar state
  useEffect(() => {
    if (user) {
      console.log('UserDetailsModal: User state updated:', {
        hasAvatar: !!user.avatarBase64,
        avatarLength: user.avatarBase64?.length || 0,
        firstName: user.firstName,
        lastName: user.lastName
      });
    }
  }, [user]);

  const loadUserDetails = async () => {
    if (!alert?.customerId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('UserDetailsModal: Loading user details for customerId:', alert.customerId);
      const response = await adminApiService.getCustomerById(alert.customerId);
      console.log('UserDetailsModal: Customer details API response:', response);
      
      if (response.success && response.data) {
        console.log('UserDetailsModal: Customer data received:', {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          avatarBase64: response.data.avatarBase64 ? 'PRESENT' : 'MISSING',
          avatarBase64Length: response.data.avatarBase64?.length || 0,
          userType: response.data.userType
        });
        setUser(response.data);
      } else {
        console.error('UserDetailsModal: API response error:', response.error);
        setError(response.error || 'Failed to load user details');
      }
    } catch (err) {
      console.error('UserDetailsModal: Exception loading user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const getKycKybSummary = (): KycKybSummary => {
    // This would be populated from actual KYC/KYB data
    // For now, returning mock data based on alert type
    const isKyc = alert?.type?.toLowerCase().includes('kyc');
    const isKyb = alert?.type?.toLowerCase().includes('kyb');
    
    return {
      hasUtilityBill: isKyc || false,
      hasPassport: isKyc || false,
      hasIdCard: isKyc || false,
      hasMemorandum: isKyb || false,
      hasArticles: isKyb || false,
      hasBusinessUtilityBill: isKyb || false,
      submittedDate: alert?.createdAt,
      status: alert?.status || 'PENDING'
    };
  };

  const handleAssign = async () => {
    if (!assigneeId.trim() || !alert) return;
    
    setActionLoading(true);
    try {
      await onAssign(alert.id.toString(), assigneeId);
      setAssigneeId('');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async (status: string) => {
    if (!alert) return;
    
    setActionLoading(true);
    try {
      await onUpdateStatus(alert.id, status, notes);
      setNotes('');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED': return 'status-badge approved';
      case 'REJECTED': return 'status-badge rejected';
      case 'PENDING': return 'status-badge pending';
      default: return 'status-badge';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isBusinessAccount = () => {
    // Check if it's a business account based on userType
    return user?.userType === 'BUSINESS_NORMAL' || user?.userType === 'BUSINESS_HIGH_RISK';
  };

  const getAccountTypeDisplay = () => {
    if (isBusinessAccount()) {
      return {
        type: 'Business',
        icon: '🏢',
        badgeClass: 'business-badge',
        avatarClass: 'business-avatar',
        iconClass: 'business-icon'
      };
    } else {
      return {
        type: 'Individual',
        icon: '👤',
        badgeClass: 'individual-badge',
        avatarClass: 'individual-avatar',
        iconClass: 'individual-icon'
      };
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Loading user details...</p>
          </div>
        ) : error ? (
          <div className="modal-error">
            <div className="error-icon">⚠️</div>
            <h3>Error Loading User Details</h3>
            <p>{error}</p>
            <button className="retry-btn" onClick={loadUserDetails}>
              Try Again
            </button>
          </div>
        ) : user ? (
          <>
            {/* Header */}
            <div className="modal-header">
              <div className="user-profile-header">
                <div className="user-avatar">
                  {user.avatarBase64 ? (
                    <img 
                      src={`data:image/jpeg;base64,${user.avatarBase64}`} 
                      alt="Profile" 
                      onError={(e) => {
                        console.error('UserDetailsModal: Avatar image failed to load:', e);
                        // Fallback to default avatar if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                      onLoad={() => {
                        console.log('UserDetailsModal: Avatar image loaded successfully');
                      }}
                    />
                  ) : null}
                  <div className={`default-avatar ${user.avatarBase64 ? 'hidden' : ''} ${getAccountTypeDisplay().avatarClass}`}>
                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                  </div>
                  {/* Account Type Icon */}
                  <div className={`account-type-icon ${getAccountTypeDisplay().iconClass}`}>
                    {getAccountTypeDisplay().icon}
                  </div>
                </div>
                <div className="user-info">
                  <div className="user-name-section">
                    <h2>{user.firstName} {user.lastName}</h2>
                    <span className={`account-type-badge ${getAccountTypeDisplay().badgeClass}`}>
                      {getAccountTypeDisplay().icon} {getAccountTypeDisplay().type}
                    </span>
                  </div>
                  <p className="user-email">{user.email}</p>
                  <p className="user-account-type">
                    {getAccountTypeDisplay().type} Account
                  </p>
                </div>
                <div className="status-section">
                  <span className={getStatusBadgeClass(alert?.status || 'PENDING')}>
                    {alert?.status || 'PENDING'}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={onClose}>×</button>
            </div>

            {/* Tabs */}
            <div className="modal-tabs">
              <button
                className={activeTab === 'personal' ? 'tab-btn active' : 'tab-btn'}
                onClick={() => setActiveTab('personal')}
              >
                Personal Info
              </button>
              <button
                className={activeTab === 'documents' ? 'tab-btn active' : 'tab-btn'}
                onClick={() => setActiveTab('documents')}
              >
                Documents
              </button>
              <button
                className={activeTab === 'activity' ? 'tab-btn active' : 'tab-btn'}
                onClick={() => setActiveTab('activity')}
              >
                Activity
              </button>
            </div>

            {/* Content */}
            <div className="modal-body">
              {activeTab === 'personal' && (
                <div className="personal-info">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Full Name</label>
                      <span>{user.firstName} {user.lastName}</span>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <span>{user.email}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone</label>
                      <span>{user.phone || 'Not provided'}</span>
                    </div>
                    <div className="info-item">
                      <label>Date of Birth</label>
                      <span>{user.dateOfBirth ? formatDate(user.dateOfBirth) : 'Not provided'}</span>
                    </div>
                    <div className="info-item full-width">
                      <label>Address</label>
                      <span>
                        {user.address || 'Not provided'}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Account Type</label>
                      <span>{user.accountType === 'PARENT' ? 'Business' : 'Individual'}</span>
                    </div>
                    <div className="info-item">
                      <label>Registration Date</label>
                      <span>{user.createdAt ? formatDate(user.createdAt) : 'Not available'}</span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'documents' && (
                <div className="documents-info">
                  <div className="document-summary">
                    <h3>Submitted Documents</h3>
                    {(() => {
                      const summary = getKycKybSummary();
                      const isKyc = alert?.type?.toLowerCase().includes('kyc');
                      const isKyb = alert?.type?.toLowerCase().includes('kyb');
                      
                      return (
                        <div className="document-list">
                          {isKyc && (
                            <>
                              <div className="document-item">
                                <span className="document-icon">📄</span>
                                <span className="document-name">Utility Bill</span>
                                <span className={`document-status ${summary.hasUtilityBill ? 'submitted' : 'missing'}`}>
                                  {summary.hasUtilityBill ? '✅ Submitted' : '❌ Missing'}
                                </span>
                              </div>
                              <div className="document-item">
                                <span className="document-icon">🛂</span>
                                <span className="document-name">Passport</span>
                                <span className={`document-status ${summary.hasPassport ? 'submitted' : 'missing'}`}>
                                  {summary.hasPassport ? '✅ Submitted' : '❌ Missing'}
                                </span>
                              </div>
                              <div className="document-item">
                                <span className="document-icon">🆔</span>
                                <span className="document-name">ID Card</span>
                                <span className={`document-status ${summary.hasIdCard ? 'submitted' : 'missing'}`}>
                                  {summary.hasIdCard ? '✅ Submitted' : '❌ Missing'}
                                </span>
                              </div>
                            </>
                          )}
                          
                          {isKyb && (
                            <>
                              <div className="document-item">
                                <span className="document-icon">📋</span>
                                <span className="document-name">Memorandum of Association</span>
                                <span className={`document-status ${summary.hasMemorandum ? 'submitted' : 'missing'}`}>
                                  {summary.hasMemorandum ? '✅ Submitted' : '❌ Missing'}
                                </span>
                              </div>
                              <div className="document-item">
                                <span className="document-icon">📜</span>
                                <span className="document-name">Articles of Association</span>
                                <span className={`document-status ${summary.hasArticles ? 'submitted' : 'missing'}`}>
                                  {summary.hasArticles ? '✅ Submitted' : '❌ Missing'}
                                </span>
                              </div>
                              <div className="document-item">
                                <span className="document-icon">🏢</span>
                                <span className="document-name">Business Utility Bill</span>
                                <span className={`document-status ${summary.hasBusinessUtilityBill ? 'submitted' : 'missing'}`}>
                                  {summary.hasBusinessUtilityBill ? '✅ Submitted' : '❌ Missing'}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="activity-info">
                  <div className="activity-timeline">
                    <div className="timeline-item">
                      <div className="timeline-icon">📝</div>
                      <div className="timeline-content">
                        <h4>Document Submission</h4>
                        <p>User submitted {alert?.type} documents</p>
                        <span className="timeline-date">
                          {alert?.createdAt ? formatDate(alert.createdAt) : 'Unknown date'}
                        </span>
                      </div>
                    </div>
                    {alert?.assignee && (
                      <div className="timeline-item">
                        <div className="timeline-icon">👤</div>
                        <div className="timeline-content">
                          <h4>Assigned to Admin</h4>
                          <p>Alert assigned to {alert.assignee}</p>
                          <span className="timeline-date">Recently</span>
                        </div>
                      </div>
                    )}
                    {alert?.status !== 'OPEN' && alert?.status !== 'IN_PROGRESS' && (
                      <div className="timeline-item">
                        <div className="timeline-icon">
                          {alert?.status === 'RESOLVED' ? '✅' : '❌'}
                        </div>
                        <div className="timeline-content">
                          <h4>Status Updated</h4>
                          <p>Status changed to {alert?.status}</p>
                          <span className="timeline-date">Recently</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="modal-footer">
              <div className="action-section">
                {!alert?.assignee && (
                  <div className="assign-section">
                    <input
                      type="text"
                      placeholder="Enter assignee ID"
                      value={assigneeId}
                      onChange={(e) => setAssigneeId(e.target.value)}
                      className="assign-input"
                    />
                    <button
                      className="action-btn primary"
                      onClick={handleAssign}
                      disabled={actionLoading || !assigneeId.trim()}
                    >
                      {actionLoading ? 'Assigning...' : 'Assign'}
                    </button>
                  </div>
                )}
                
                {alert?.status === 'OPEN' && (
                  <div className="status-actions">
                    <button
                      className="action-btn success"
                      onClick={() => handleStatusUpdate('APPROVED')}
                      disabled={actionLoading}
                    >
                      ✅ Approve
                    </button>
                    <button
                      className="action-btn danger"
                      onClick={() => handleStatusUpdate('REJECTED')}
                      disabled={actionLoading}
                    >
                      ❌ Reject
                    </button>
                  </div>
                )}
              </div>
              
              <div className="notes-section">
                <textarea
                  placeholder="Add notes or comments..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="notes-input"
                  rows={2}
                />
              </div>
              
              <button className="action-btn secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default UserDetailsModal; 